import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, IconButton, Tooltip, Chip } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, ContentCopy as CopyIcon, Block as BlockIcon, Edit as EditIcon, Check as CheckIcon } from '@mui/icons-material';

interface JsonDiffViewerProps {
  currentData: any;
  incomingData: any;
  recordIndex: number;
  recordType?: string;
  onDataChange?: (field: string, value: any) => void;
  editable?: boolean;
}

const JsonDiffViewer: React.FC<JsonDiffViewerProps> = ({
  currentData,
  incomingData,
  recordIndex,
  recordType = 'Record',
  onDataChange,
  editable = false
}) => {
  console.log('JsonDiffViewer rendered with editable:', editable, 'onDataChange:', !!onDataChange, 'currentData:', currentData, 'incomingData:', incomingData);
  // Local state for incoming data
  const [localIncomingData, setLocalIncomingData] = useState(incomingData);
  // State for tracking which fields are being edited
  const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
  // State for tracking temporary edit values
  const [tempEditValues, setTempEditValues] = useState<Record<string, any>>({});
  // Refs for input fields
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Update local data when incoming data changes
  React.useEffect(() => {
    setLocalIncomingData(incomingData);
  }, [incomingData]);

  // Helper function to start editing a field
  const startEditing = (field: string, currentValue: any) => {
    setEditingFields(prev => new Set(prev).add(field));
    setTempEditValues(prev => ({
      ...prev,
      [field]: formatValueForEdit(currentValue)
    }));
    
    // Focus the input immediately
    setTimeout(() => {
      const inputElement = inputRefs.current[field];
      if (inputElement) {
        inputElement.focus();
        inputElement.select();
      }
    }, 10);
  };

  // Helper function to cancel editing
  const cancelEditing = (field: string) => {
    setEditingFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(field);
      return newSet;
    });
    setTempEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
  };

  // Helper function to save editing
  const saveEditing = (field: string) => {
    const tempValue = tempEditValues[field];
    if (tempValue !== undefined) {
      const parsedValue = parseValueFromEdit(tempValue);
      
      // Update local data
      setLocalIncomingData(prev => ({
        ...prev,
        [field]: parsedValue
      }));

      // Call parent callback
      if (onDataChange) {
        onDataChange(field, parsedValue);
      }
    }
    
    // Stop editing
    setEditingFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(field);
      return newSet;
    });
    setTempEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
  };

  // Helper function to format value for editing
  const formatValueForEdit = (value: any) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return value; // Don't add quotes for editing
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'number') return value.toString();
    if (Array.isArray(value)) return JSON.stringify(value, null, 2);
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Helper function to parse value from edit
  const parseValueFromEdit = (value: string) => {
    if (value === 'null') return null;
    if (value === 'undefined') return undefined;
    if (value === 'true') return true;
    if (value === 'false') return false;
    
    // Try to parse as number
    if (!isNaN(Number(value)) && value !== '') {
      return Number(value);
    }
    
    // Try to parse as JSON
    try {
      return JSON.parse(value);
    } catch {
      // Return as string if not valid JSON
      return value;
    }
  };

  // Function to create a unified field order for both objects
  const createUnifiedFieldOrder = (obj1: any, obj2: any) => {
    const allFields = new Set<string>();
    if (obj1) Object.keys(obj1).forEach(key => allFields.add(key));
    if (obj2) Object.keys(obj2).forEach(key => allFields.add(key));
    return Array.from(allFields).sort();
  };

  // Function to compare two values and determine the change type
  const getChangeType = (currentValue: any, incomingValue: any) => {
    if (currentValue === undefined && incomingValue !== undefined) {
      return 'added';
    }
    if (currentValue !== undefined && incomingValue === undefined) {
      return 'removed';
    }
    if (JSON.stringify(currentValue) !== JSON.stringify(incomingValue)) {
      return 'modified';
    }
    return 'unchanged';
  };

  // Function to format value for display
  const formatValue = (value: any) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value}"`;
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'number') return value.toString();
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'object') {
      if (Object.keys(value).length === 0) return '{}';
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Function to check if value is complex (array or object)
  const isComplexValue = (value: any) => {
    return (Array.isArray(value) && value.length > 0) || 
           (typeof value === 'object' && value !== null && Object.keys(value).length > 0);
  };

  // Function to render complex values (arrays and objects) with individual field editing
  const renderComplexValue = (field: string, value: any, changeType: string, color: string, isIncomingData: boolean = false, currentValue?: any) => {
    const isCurrentlyEditing = editingFields.has(field);
    const displayValue = localIncomingData?.[field] !== undefined ? localIncomingData[field] : value;

    // If not editable, show read-only complex values
    if (!editable) {
      return (
        <Box sx={{ 
          color: color,
          wordBreak: 'break-all',
          minWidth: 0,
          flex: 1,
          padding: '8px',
          borderRadius: '3px',
          transition: 'all 0.15s ease',
          backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.05)' : 
                          changeType === 'removed' ? 'rgba(215, 58, 73, 0.05)' : 
                          'transparent',
          border: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.2)' : 
                 changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.2)' : 
                 '1px solid transparent'
        }}>
          <pre style={{ 
            margin: 0, 
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap'
          }}>
            {formatValue(displayValue)}
          </pre>
        </Box>
      );
    }

    if (isCurrentlyEditing) {
      // Editing mode for complex values
      return (
        <TextField
          inputRef={(el) => {
            inputRefs.current[field] = el;
          }}
          value={tempEditValues[field] || ''}
          placeholder={`Edit ${field}...`}
          onChange={(e) => {
            setTempEditValues(prev => ({
              ...prev,
              [field]: e.target.value
            }));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              saveEditing(field);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditing(field);
            } else if (e.key === 'Tab') {
              e.preventDefault();
              saveEditing(field);
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              if (editingFields.has(field)) {
                saveEditing(field);
              }
            }, 100);
          }}
          size="small"
          variant="outlined"
          autoFocus
          multiline
          rows={Math.min(10, (formatValue(displayValue).split('\n').length || 1))}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8rem',
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
              padding: '8px',
              backgroundColor: changeType === 'added' ? '#e6ffed' : 
                             changeType === 'removed' ? '#ffeef0' : 
                             '#ffffff',
              borderRadius: '3px',
              transition: 'all 0.15s ease',
              '& fieldset': {
                borderColor: changeType === 'added' ? '#28a745' : 
                            changeType === 'removed' ? '#d73a49' : 
                            '#d0d7de',
                borderWidth: '2px',
                transition: 'all 0.15s ease'
              },
              '&:hover fieldset': {
                borderColor: changeType === 'added' ? '#28a745' : 
                            changeType === 'removed' ? '#d73a49' : 
                            '#656d76',
                borderWidth: '2px'
              },
              '&.Mui-focused fieldset': {
                borderColor: changeType === 'added' ? '#28a745' : 
                            changeType === 'removed' ? '#d73a49' : 
                            '#0969da',
                borderWidth: '2px',
                boxShadow: changeType === 'added' ? '0 0 0 2px rgba(40, 167, 69, 0.2)' : 
                          changeType === 'removed' ? '0 0 0 2px rgba(215, 58, 73, 0.2)' : 
                          '0 0 0 2px rgba(9, 105, 218, 0.2)'
              }
            }
          }}
        />
      );
    } else {
      // Display mode for complex values
      return (
        <Box sx={{ 
          color: color,
          wordBreak: 'break-all',
          minWidth: 0,
          flex: 1,
          padding: '8px',
          borderRadius: '3px',
          cursor: 'text',
          transition: 'all 0.15s ease',
          position: 'relative',
          backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.05)' : 
                          changeType === 'removed' ? 'rgba(215, 58, 73, 0.05)' : 
                          'transparent',
          border: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.2)' : 
                 changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.2)' : 
                 '1px solid transparent',
          '&:hover': {
            backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.08)' : 
                            changeType === 'removed' ? 'rgba(215, 58, 73, 0.08)' : 
                            'rgba(0, 0, 0, 0.05)',
            outline: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.3)' : 
                    changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.3)' : 
                    '1px solid rgba(0, 0, 0, 0.2)',
            transform: 'translateY(-1px)'
          },
          '&:active': {
            transform: 'translateY(0)',
            backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.12)' : 
                           changeType === 'removed' ? 'rgba(215, 58, 73, 0.12)' : 
                           'rgba(0, 0, 0, 0.08)'
          }
        }}
        onClick={() => startEditing(field, displayValue)}
        >
          <pre style={{ 
            margin: 0, 
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap'
          }}>
            {formatValue(displayValue)}
          </pre>
        </Box>
      );
    }
  };

  // Function to render field value with Cursor AI-like editing capability
  const renderFieldValue = (field: string, value: any, isEditing: boolean, changeType: string, color: string, isIncomingData: boolean = false, currentValue?: any) => {
    const isCurrentlyEditing = editingFields.has(field);
    // For non-editable mode (confirmation step), always use the passed value
    // For editable mode, use localIncomingData if available
    const displayValue = editable && localIncomingData?.[field] !== undefined ? localIncomingData[field] : value;
    
    // Check if this is a complex value (array or object)
    if (isComplexValue(displayValue)) {
      return renderComplexValue(field, displayValue, changeType, color, isIncomingData, currentValue);
    }
    
    // If not editable, show read-only values
    if (!editable) {
      return (
        <Box sx={{ 
          color: color,
          wordBreak: 'break-all',
          minWidth: 0,
          flex: 1,
          padding: '2px 4px',
          borderRadius: '3px',
          transition: 'all 0.15s ease'
        }}>
          {formatValue(displayValue)}
        </Box>
      );
    }
    
    // For simple fields - show editable field
    if (isIncomingData || changeType === 'unchanged' || changeType === 'added') {
      return (
        <Box sx={{ 
          flex: 1,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          position: 'relative'
        }}>
          {isCurrentlyEditing ? (
            // Editing mode - show input field (Cursor AI style)
            <TextField
              inputRef={(el) => {
                inputRefs.current[field] = el;
              }}
              value={tempEditValues[field] || ''}
              placeholder={`Edit ${field}...`}
              onChange={(e) => {
                setTempEditValues(prev => ({
                  ...prev,
                  [field]: e.target.value
                }));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveEditing(field);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEditing(field);
                } else if (e.key === 'Tab') {
                  e.preventDefault();
                  saveEditing(field);
                }
              }}
              onBlur={() => {
                // Auto-save on blur
                setTimeout(() => {
                  if (editingFields.has(field)) {
                    saveEditing(field);
                  }
                }, 100);
              }}
              size="small"
              variant="outlined"
              autoFocus
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8rem',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                  padding: '2px 8px',
                  height: '20px',
                  backgroundColor: changeType === 'added' ? '#e6ffed' : 
                                 changeType === 'removed' ? '#ffeef0' : 
                                 '#ffffff',
                  borderRadius: '3px',
                  transition: 'all 0.15s ease',
                  '& fieldset': {
                    borderColor: changeType === 'added' ? '#28a745' : 
                                changeType === 'removed' ? '#d73a49' : 
                                '#d0d7de',
                    borderWidth: '2px',
                    transition: 'all 0.15s ease'
                  },
                  '&:hover fieldset': {
                    borderColor: changeType === 'added' ? '#28a745' : 
                                changeType === 'removed' ? '#d73a49' : 
                                '#656d76',
                    borderWidth: '2px'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: changeType === 'added' ? '#28a745' : 
                                changeType === 'removed' ? '#d73a49' : 
                                '#0969da',
                    borderWidth: '2px',
                    boxShadow: changeType === 'added' ? '0 0 0 2px rgba(40, 167, 69, 0.2)' : 
                              changeType === 'removed' ? '0 0 0 2px rgba(215, 58, 73, 0.2)' : 
                              '0 0 0 2px rgba(9, 105, 218, 0.2)'
                  }
                }
              }}
            />
          ) : (
            // Display mode - click to edit (Cursor AI style)
            <Box sx={{ 
              color: color,
              wordBreak: 'break-all',
              minWidth: 0,
              flex: 1,
              padding: '2px 4px',
              borderRadius: '3px',
              cursor: 'text',
              transition: 'all 0.15s ease',
              position: 'relative',
              '&:hover': {
                backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.08)' : 
                              changeType === 'removed' ? 'rgba(215, 58, 73, 0.08)' : 
                              'rgba(0, 0, 0, 0.05)',
                outline: changeType === 'added' ? '1px solid rgba(40, 167, 69, 0.3)' : 
                        changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.3)' : 
                        '1px solid rgba(0, 0, 0, 0.2)',
              },
              '&:active': {
                backgroundColor: changeType === 'added' ? 'rgba(40, 167, 69, 0.12)' : 
                               changeType === 'removed' ? 'rgba(215, 58, 73, 0.12)' : 
                               'rgba(0, 0, 0, 0.08)'
              }
            }}
            onClick={() => startEditing(field, displayValue)}
            >
              {formatValue(displayValue)}
            </Box>
          )}
        </Box>
      );
    }

    // For current data (red lines) - show value with ignore button
    return (
      <Box sx={{ 
        flex: 1,
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        gap: '4px'
      }}>
        {!editable ? (
          // Read-only mode
          <Box sx={{ 
            color: color,
            wordBreak: 'break-all',
            minWidth: 0,
            flex: 1,
            padding: isComplexValue(value) ? '8px' : '2px 4px',
            borderRadius: '3px',
            transition: 'all 0.15s ease',
            backgroundColor: isComplexValue(value) ? (changeType === 'removed' ? 'rgba(215, 58, 73, 0.05)' : 'transparent') : 'transparent',
            border: isComplexValue(value) ? (changeType === 'removed' ? '1px solid rgba(215, 58, 73, 0.2)' : '1px solid transparent') : 'none'
          }}>
            {isComplexValue(value) ? (
              <pre style={{ 
                margin: 0, 
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                fontSize: '0.8rem',
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap'
              }}>
                {formatValue(value)}
              </pre>
            ) : (
              formatValue(value)
            )}
          </Box>
        ) : isComplexValue(value) ? (
          <Box sx={{ 
            color: color,
            wordBreak: 'break-all',
            minWidth: 0,
            flex: 1,
            padding: '8px',
            borderRadius: '3px',
            transition: 'all 0.15s ease',
            cursor: 'text',
            backgroundColor: 'rgba(215, 58, 73, 0.05)',
            border: '1px solid rgba(215, 58, 73, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(215, 58, 73, 0.08)',
              outline: '1px solid rgba(215, 58, 73, 0.3)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              backgroundColor: 'rgba(215, 58, 73, 0.12)'
            }
          }}
          onClick={() => startEditing(field, value)}
          >
            <pre style={{ 
              margin: 0, 
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
              fontSize: '0.8rem',
              lineHeight: '1.4',
              whiteSpace: 'pre-wrap'
            }}>
              {formatValue(value)}
            </pre>
          </Box>
        ) : (
          <Box sx={{ 
            color: color,
            wordBreak: 'break-all',
            minWidth: 0,
            flex: 1,
            padding: '2px 4px',
            borderRadius: '3px',
            transition: 'all 0.15s ease',
            cursor: 'text',
            '&:hover': {
              backgroundColor: 'rgba(215, 58, 73, 0.08)',
              outline: '1px solid rgba(215, 58, 73, 0.3)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              backgroundColor: 'rgba(215, 58, 73, 0.12)'
            }
          }}
          onClick={() => startEditing(field, value)}
          >
            {formatValue(value)}
          </Box>
        )}
        {editable && currentValue !== undefined && (
          <Tooltip title="Use Current Value (Ignore Incoming)">
            <IconButton 
              size="small" 
              onClick={() => {
                console.log('Ignore button clicked - using current value instead of incoming');
                if (onDataChange) {
                  onDataChange(field, currentValue);
                }
                // Update local data
                setLocalIncomingData(prev => ({
                  ...prev,
                  [field]: currentValue
                }));
              }}
              sx={{ 
                color: '#d73a49',
                padding: '2px',
                borderRadius: '3px',
                transition: 'all 0.15s ease',
                opacity: 0.7,
                '&:hover': {
                  color: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  opacity: 1
                }
              }}
            >
              <BlockIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Function to render a single line with GitHub-style diff
  const renderDiffLine = (field: string, currentValue: any, incomingValue: any, lineNumber: number) => {
    const changeType = getChangeType(currentValue, incomingValue);
    
    
    // For modified fields, render as two separate lines (removed + added)
    if (changeType === 'modified') {
      return (
        <Box key={field}>
          {/* Removed line */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '28px',
            backgroundColor: '#ffeef0',
            borderLeft: '4px solid #d73a49',
            color: '#24292e',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: '#ffebee',
              boxShadow: 'inset 0 0 0 1px rgba(215, 58, 73, 0.2)',
            }
          }}>
            {/* Line number */}
            <Box sx={{ 
              width: '50px',
              padding: '0 8px',
              textAlign: 'right',
              color: '#656d76',
              backgroundColor: '#f6f8fa',
              borderRight: '1px solid #d0d7de',
              flexShrink: 0,
              userSelect: 'none',
              fontSize: '0.75rem'
            }}>
              {String(lineNumber).padStart(3, ' ')}
            </Box>
            
            {/* Change indicator */}
            <Box sx={{ 
              width: '24px',
              padding: '0 4px',
              textAlign: 'center',
              flexShrink: 0,
              fontWeight: 'bold',
              fontSize: '1rem',
              color: '#d73a49',
              backgroundColor: '#ffeef0'
            }}>
              -
            </Box>
            
            {/* Field name and value */}
            <Box sx={{ 
              flex: 1,
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              minWidth: 0
            }}>
              <Box sx={{ 
                fontWeight: '500',
                color: '#24292e',
                marginRight: '8px',
                flexShrink: 0,
                fontSize: '0.8rem'
              }}>
                {field}:
              </Box>
              {renderFieldValue(field, currentValue, true, changeType, '#d73a49', false, currentValue)}
            </Box>
          </Box>
          
          {/* Added line */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: '0.8rem',
            lineHeight: '28px',
            backgroundColor: '#e6ffed',
            borderLeft: '4px solid #28a745',
            color: '#24292e',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: '#f0fff4',
              boxShadow: 'inset 0 0 0 1px rgba(40, 167, 69, 0.2)',
            }
          }}>
            {/* Line number */}
            <Box sx={{ 
              width: '50px',
              padding: '0 8px',
              textAlign: 'right',
              color: '#656d76',
              backgroundColor: '#f6f8fa',
              borderRight: '1px solid #d0d7de',
              flexShrink: 0,
              userSelect: 'none',
              fontSize: '0.75rem'
            }}>
              {String(lineNumber + 1).padStart(3, ' ')}
            </Box>
            
            {/* Change indicator */}
            <Box sx={{ 
              width: '24px',
              padding: '0 4px',
              textAlign: 'center',
              flexShrink: 0,
              fontWeight: 'bold',
              fontSize: '1rem',
              color: '#28a745',
              backgroundColor: '#e6ffed'
            }}>
              +
            </Box>
            
            {/* Field name and value */}
            <Box sx={{ 
              flex: 1,
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              minWidth: 0
            }}>
              <Box sx={{ 
                fontWeight: '500',
                color: '#24292e',
                marginRight: '8px',
                flexShrink: 0,
                fontSize: '0.8rem'
              }}>
                {field}:
              </Box>
              {renderFieldValue(field, incomingValue, true, changeType, '#28a745', true, currentValue)}
            </Box>
          </Box>
        </Box>
      );
    }
    
    // For added, removed, and unchanged fields
    const getLineStyle = () => {
      switch (changeType) {
        case 'added':
          return { 
            backgroundColor: '#e6ffed', 
            borderLeft: '4px solid #28a745',
            color: '#24292e',
            '&:hover': {
              backgroundColor: '#f0fff4',
              boxShadow: 'inset 0 0 0 1px rgba(40, 167, 69, 0.2)',
            }
          };
        case 'removed':
          return { 
            backgroundColor: '#ffeef0', 
            borderLeft: '4px solid #d73a49',
            color: '#24292e',
            '&:hover': {
              backgroundColor: '#ffebee',
              boxShadow: 'inset 0 0 0 1px rgba(215, 58, 73, 0.2)',
            }
          };
        default:
          return { 
            backgroundColor: '#ffffff', 
            borderLeft: '4px solid transparent',
            color: '#24292e',
            '&:hover': {
              backgroundColor: '#f6f8fa',
              boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
            }
          };
      }
    };

    const lineStyle = getLineStyle();
    const paddedLineNumber = String(lineNumber).padStart(3, ' ');

    return (
      <Box 
        key={field}
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          minHeight: '28px',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '0.8rem',
          lineHeight: '28px',
          transition: 'all 0.15s ease',
          ...lineStyle
        }}
      >
        {/* Line number */}
        <Box sx={{ 
          width: '50px',
          padding: '0 8px',
          textAlign: 'right',
          color: '#656d76',
          backgroundColor: '#f6f8fa',
          borderRight: '1px solid #d0d7de',
          flexShrink: 0,
          userSelect: 'none',
          fontSize: '0.75rem'
        }}>
          {paddedLineNumber}
        </Box>
        
        {/* Change indicator - GitHub style */}
        <Box sx={{ 
          width: '24px',
          padding: '0 4px',
          textAlign: 'center',
          flexShrink: 0,
          fontWeight: 'bold',
          fontSize: '1rem',
          color: changeType === 'added' ? '#28a745' : 
                 changeType === 'removed' ? '#d73a49' : '#656d76',
          backgroundColor: changeType === 'added' ? '#e6ffed' : 
                          changeType === 'removed' ? '#ffeef0' : 'transparent'
        }}>
          {changeType === 'added' ? '+' : 
           changeType === 'removed' ? '-' : ' '}
        </Box>
        
        {/* Field name and value */}
        <Box sx={{ 
          flex: 1,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          minWidth: 0
        }}>
          <Box sx={{ 
            fontWeight: '500',
            color: '#24292e',
            marginRight: '8px',
            flexShrink: 0,
            fontSize: '0.8rem'
          }}>
            {field}:
          </Box>
          {renderFieldValue(
            field, 
            changeType === 'added' ? incomingValue : currentValue, 
            true, 
            changeType, 
            changeType === 'added' ? '#28a745' : 
            changeType === 'removed' ? '#d73a49' : '#24292e',
            true, // All fields should be editable
            currentValue
          )}
        </Box>
      </Box>
    );
  };

  // Get unified field order
  const unifiedFields = createUnifiedFieldOrder(currentData, incomingData);

  // If no data, show a test case with sample data
  if (!currentData && !incomingData) {
    const testCurrentData = {
      title: "Old Title",
      vehicle_make: "Toyota",
      vehicle_year: 2023,
      removed_field: "This will be removed",
      eligible_regions: ["US", "CA"],
      finance_terms: {
        interest_rate: 3.5,
        term_months: 60
      },
      features: ["GPS", "Bluetooth", "Backup Camera"]
    };
    
    const testIncomingData = {
      title: "New Title",
      vehicle_make: "Honda", 
      vehicle_year: 2024,
      new_field: "Added Value",
      eligible_regions: ["US", "CA", "MX"],
      finance_terms: {
        interest_rate: 2.9,
        term_months: 72,
        down_payment: 5000
      },
      features: ["GPS", "Bluetooth", "Backup Camera", "Lane Assist"]
    };
    
    return (
      <Box sx={{ 
        border: '1px solid #d0d7de',
        borderRadius: '6px',
        overflow: 'hidden',
        mb: 2,
        backgroundColor: '#ffffff'
      }}>
        <Box sx={{ 
          backgroundColor: '#f6f8fa',
          padding: '8px 16px',
          borderBottom: '1px solid #d0d7de',
          fontSize: '0.8rem',
          fontWeight: '600',
          color: '#24292e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box>
            Test Record - Sample Data
          </Box>
          <Box sx={{ 
            fontSize: '0.75rem',
            color: '#656d76',
            fontWeight: '400'
          }}>
            Updated Record
          </Box>
        </Box>
        
        {/* Test diff content */}
        <Box sx={{ 
          backgroundColor: '#ffffff',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '0.8rem',
          lineHeight: '28px'
        }}>
          {Object.keys({...testCurrentData, ...testIncomingData}).sort().map((field, index) => 
            renderDiffLine(
              field, 
              testCurrentData[field], 
              testIncomingData[field],
              index + 1
            )
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      border: '1px solid #d0d7de',
      borderRadius: '6px',
      overflow: 'hidden',
      mb: 2,
      backgroundColor: '#ffffff'
    }}>
      {/* Record Header - GitHub style */}
      <Box sx={{ 
        backgroundColor: '#f6f8fa',
        padding: '8px 16px',
        borderBottom: '1px solid #d0d7de',
        fontSize: '0.8rem',
        fontWeight: '600',
        color: '#24292e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box>
          {recordType} {recordIndex + 1}
        </Box>
        <Box sx={{ 
          fontSize: '0.75rem',
          color: '#656d76',
          fontWeight: '400'
        }}>
          {!currentData ? 'New Record' : 'Updated Record'}
        </Box>
      </Box>
      
      {/* Diff Content - GitHub style */}
      <Box sx={{ 
        backgroundColor: '#ffffff',
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
        fontSize: '0.8rem',
        lineHeight: '28px'
      }}>
        {unifiedFields.map((field, index) => 
          renderDiffLine(
            field, 
            currentData?.[field], 
            incomingData?.[field],
            index + 1
          )
        )}
      </Box>
    </Box>
  );
};

export default JsonDiffViewer;